import { useState, useEffect } from 'react';

interface OutlineSlide {
  index: number;
  title: string;
  type: string;
  icon: string;
  points: string[];
  color: string;
}

const PRESENTATION_GOALS = [
  { value: 'pitch',      label: 'Startup Pitch',     icon: 'ri-rocket-2-line',           color: '#e11d48' },
  { value: 'sales',      label: 'Sales Deck',        icon: 'ri-money-dollar-circle-line', color: '#059669' },
  { value: 'education',  label: 'Educational Talk',  icon: 'ri-graduation-cap-line',      color: '#d97706' },
  { value: 'report',     label: 'Business Report',   icon: 'ri-file-chart-line',          color: '#0891b2' },
  { value: 'investor',   label: 'Investor Deck',     icon: 'ri-line-chart-line',          color: '#7c3aed' },
  { value: 'training',   label: 'Training / Workshop', icon: 'ri-team-line',              color: '#db2777' },
  { value: 'conference', label: 'Conference Talk',   icon: 'ri-mic-line',                 color: '#64748b' },
];

const AUDIENCE_TYPES = [
  'Executives', 'Investors', 'Customers', 'Students', 'General Audience', 'Team Members', 'Partners',
];

// ── Deterministic outline generator ─────────────────────────────────────────
function generateOutline(topic: string, goal: string, slideCount: number, audience: string): OutlineSlide[] {
  const topicWords = topic.trim().split(/\s+/).filter(Boolean);
  const mainTopic = topicWords.length > 0 ? topic.trim() : 'Your Topic';

  const goalData = PRESENTATION_GOALS.find(g => g.value === goal) ?? PRESENTATION_GOALS[0];

  // Template structures by goal
  const structuresByGoal: Record<string, Array<{ type: string; icon: string; getTitleFn: (t: string) => string; getPointsFn: (t: string, a: string) => string[] }>> = {
    pitch: [
      { type: 'Cover',          icon: 'ri-home-3-line',          getTitleFn: t => `${t} — The Future Starts Here`,                                    getPointsFn: (_t, a) => [`One-liner value proposition`, `Tagline crafted for ${a}`, `Founding team quick intro`] },
      { type: 'Problem',        icon: 'ri-alert-line',           getTitleFn: t => `The Problem With ${t} Today`,                                      getPointsFn: (t, _a) => [`Pain points costing industry millions`, `Current solutions fall short for ${t}`, `The gap nobody has solved yet`] },
      { type: 'Solution',       icon: 'ri-lightbulb-line',       getTitleFn: t => `Introducing the ${t} Platform`,                                    getPointsFn: (_t, _a) => [`Core product overview`, `How it works in 3 steps`, `Why our approach is defensible`] },
      { type: 'Market Size',    icon: 'ri-global-line',          getTitleFn: t => `The ${t} Market Opportunity`,                                      getPointsFn: (_t, _a) => [`Total Addressable Market (TAM): $X billion`, `Serviceable Obtainable Market (SOM)', 'CAGR over next 5 years`] },
      { type: 'Product Demo',   icon: 'ri-computer-line',        getTitleFn: t => `How ${t} Works`,                                                   getPointsFn: (_t, _a) => [`Step-by-step user journey`, `Key features walkthrough`, `Live results / demo screenshot`] },
      { type: 'Traction',       icon: 'ri-bar-chart-box-line',   getTitleFn: _t => `Traction & Validation`,                                           getPointsFn: (_t, _a) => [`Month-over-month growth metrics`, `Key customer logos`, `Notable press & partnerships`] },
      { type: 'Business Model', icon: 'ri-money-dollar-circle-line', getTitleFn: t => `${t} Revenue Model`,                                           getPointsFn: (_t, _a) => [`Primary revenue stream`, `Unit economics & LTV/CAC ratio`, `Path to profitability`] },
      { type: 'Competition',    icon: 'ri-scales-3-line',        getTitleFn: t => `Why ${t} Wins`,                                                    getPointsFn: (_t, _a) => [`Competitive landscape overview`, `Our key differentiators`, `Defensible moat & barriers to entry`] },
      { type: 'Team',           icon: 'ri-team-line',            getTitleFn: _t => `The Team Behind It`,                                              getPointsFn: (_t, _a) => [`Founders & domain expertise`, `Advisory board highlights`, `Why we're uniquely positioned to win`] },
      { type: 'Roadmap',        icon: 'ri-road-map-line',        getTitleFn: _t => `12-Month Product Roadmap`,                                        getPointsFn: (_t, _a) => [`Q1–Q2: Core feature launches`, `Q3: Expansion & integrations`, `Q4: Scale & enterprise tier`] },
      { type: 'Financials',     icon: 'ri-file-chart-line',      getTitleFn: _t => `Financial Projections`,                                           getPointsFn: (_t, _a) => [`3-year revenue forecast`, `Key assumptions & metrics`, `Path to break-even`] },
      { type: 'Ask',            icon: 'ri-megaphone-line',        getTitleFn: _t => `The Ask`,                                                         getPointsFn: (_t, a) => [`Raising $X at $Y valuation`, `Use of funds breakdown`, `Why invest now — call to action for ${a}`] },
    ],
    sales: [
      { type: 'Cover',          icon: 'ri-home-3-line',           getTitleFn: t => `${t} — A Better Way`,                                             getPointsFn: (_t, a) => [`Tailored for ${a}`, `Quick value proposition`, `Today\'s agenda`] },
      { type: 'Executive Summary', icon: 'ri-file-list-3-line',  getTitleFn: t => `Why ${t} Matters Now`,                                             getPointsFn: (_t, a) => [`Key challenge for ${a}`, `Business impact of inaction`, `Overview of our approach`] },
      { type: 'Problem',        icon: 'ri-alert-line',            getTitleFn: t => `Challenges in ${t}`,                                              getPointsFn: (_t, a) => [`Top 3 pain points for ${a}`, `Hidden cost of the status quo`, `Industry trend driving urgency`] },
      { type: 'Solution',       icon: 'ri-lightbulb-line',        getTitleFn: t => `Our ${t} Solution`,                                               getPointsFn: (_t, _a) => [`Core capabilities overview`, `Live demo highlights`, `Integration with your existing stack`] },
      { type: 'Case Study',     icon: 'ri-briefcase-4-line',      getTitleFn: t => `Success Story: ${t} in Action`,                                   getPointsFn: (_t, _a) => [`Customer background & challenge`, `Implementation timeline`, `Results: X% increase / $Y saved`] },
      { type: 'ROI',            icon: 'ri-bar-chart-box-line',    getTitleFn: _t => `Your ROI Calculation`,                                            getPointsFn: (_t, a) => [`Cost of current solution`, `Expected savings for ${a}`, `Payback period: < 6 months`] },
      { type: 'Comparison',     icon: 'ri-scales-3-line',         getTitleFn: t => `${t} vs. Alternatives`,                                           getPointsFn: (_t, _a) => [`Feature-by-feature comparison`, `Total cost of ownership', 'Support & SLA advantages`] },
      { type: 'Pricing',        icon: 'ri-price-tag-3-line',      getTitleFn: _t => `Pricing & Packages`,                                             getPointsFn: (_t, _a) => [`Starter, Pro, and Enterprise tiers`, `Volume discount options`, `Free trial & onboarding included`] },
      { type: 'Next Steps',     icon: 'ri-flag-line',             getTitleFn: _t => `Next Steps`,                                                     getPointsFn: (_t, a) => [`Proposed pilot timeline`, `Implementation checklist`, `Your dedicated success manager for ${a}`] },
    ],
    education: [
      { type: 'Title',          icon: 'ri-home-3-line',           getTitleFn: t => `Introduction to ${t}`,                                            getPointsFn: (_t, a) => [`Learning objectives for today`, `Who this is for: ${a}`, `Session agenda`] },
      { type: 'Objectives',     icon: 'ri-focus-3-line',          getTitleFn: t => `What You\'ll Learn About ${t}`,                                   getPointsFn: (_t, _a) => [`Core concept 1`, `Core concept 2`, `How to apply this knowledge`] },
      { type: 'Background',     icon: 'ri-history-line',          getTitleFn: t => `The History of ${t}`,                                             getPointsFn: (_t, _a) => [`Key milestones & origins`, `How it evolved over time`, `Where we are today`] },
      { type: 'Core Concept',   icon: 'ri-lightbulb-flash-line',  getTitleFn: t => `Understanding ${t}: Core Concepts`,                               getPointsFn: (_t, _a) => [`Foundational principle #1`, `Foundational principle #2`, `Common misconceptions`] },
      { type: 'Deep Dive',      icon: 'ri-microscope-line',       getTitleFn: t => `${t} Deep Dive`,                                                  getPointsFn: (_t, _a) => [`Technical breakdown`, `Real-world examples`, `Edge cases & exceptions`] },
      { type: 'Case Study',     icon: 'ri-file-search-line',      getTitleFn: t => `${t} in Practice`,                                                getPointsFn: (_t, a) => [`Real example relevant to ${a}`, `Lessons learned`, `Apply it yourself exercise`] },
      { type: 'Activity',       icon: 'ri-gamepad-line',          getTitleFn: t => `${t} — Hands-on Exercise`,                                        getPointsFn: (_t, _a) => [`Group discussion prompt`, `3-minute reflection activity`, `Share your insights`] },
      { type: 'Common Mistakes',icon: 'ri-error-warning-line',    getTitleFn: t => `Common Mistakes in ${t}`,                                         getPointsFn: (_t, _a) => [`Mistake #1 and how to avoid it`, `Mistake #2 and the right approach`, `Cheat sheet for quick reference`] },
      { type: 'Summary',        icon: 'ri-checkbox-circle-line',  getTitleFn: t => `Key Takeaways: ${t}`,                                             getPointsFn: (_t, _a) => [`Top 3 things to remember`, `Quick-reference checklist`, `Recommended next steps`] },
      { type: 'Resources',      icon: 'ri-book-open-line',        getTitleFn: t => `Learn More About ${t}`,                                           getPointsFn: (_t, a) => [`Books & papers for ${a}`, `Online courses & tutorials`, `Community & practice platforms`] },
    ],
    report: [
      { type: 'Cover',          icon: 'ri-home-3-line',           getTitleFn: t => `${t} — Report ${new Date().getFullYear()}`,                       getPointsFn: (_t, a) => [`Prepared for ${a}`, `Report date & version`, `Confidentiality notice`] },
      { type: 'Executive Summary', icon: 'ri-file-list-3-line',   getTitleFn: t => `${t} Executive Summary`,                                          getPointsFn: (_t, _a) => [`Key findings at a glance`, `Critical recommendations`, `Impact & urgency`] },
      { type: 'Methodology',    icon: 'ri-settings-3-line',       getTitleFn: t => `How We Analyzed ${t}`,                                            getPointsFn: (_t, _a) => [`Data sources & time period`, `Analysis framework used`, `Sample size & confidence level`] },
      { type: 'Data Overview',  icon: 'ri-bar-chart-box-line',    getTitleFn: t => `${t} Data Overview`,                                              getPointsFn: (_t, _a) => [`Headline metric #1`, `Headline metric #2`, `Year-over-year comparison`] },
      { type: 'Key Findings',   icon: 'ri-search-2-line',         getTitleFn: t => `Key Findings: ${t}`,                                              getPointsFn: (_t, _a) => [`Finding #1 with supporting data`, `Finding #2 with supporting data`, `Surprising insight`] },
      { type: 'Trend Analysis', icon: 'ri-line-chart-line',       getTitleFn: t => `${t} Trends`,                                                     getPointsFn: (_t, _a) => [`Upward trends to watch`, `Declining patterns`, `Forecast for next period`] },
      { type: 'Risk Assessment',icon: 'ri-shield-check-line',     getTitleFn: t => `${t} Risks & Opportunities`,                                      getPointsFn: (_t, a) => [`Top 3 risks for ${a}`, `Mitigation strategies`, `Emerging opportunities`] },
      { type: 'Recommendations',icon: 'ri-checkbox-circle-line',  getTitleFn: t => `${t} Recommendations`,                                            getPointsFn: (_t, _a) => [`Quick win #1`, `Strategic initiative #2`, `Long-term priority #3`] },
      { type: 'Next Steps',     icon: 'ri-flag-line',             getTitleFn: _t => `Action Plan & Next Steps`,                                       getPointsFn: (_t, _a) => [`Owner-assigned action items`, `30/60/90-day milestones`, `Review & reporting cadence`] },
    ],
    investor: [
      { type: 'Cover',          icon: 'ri-home-3-line',           getTitleFn: t => `${t} — Investor Briefing`,                                        getPointsFn: (_t, a) => [`For ${a} — Confidential`, `Company tagline`, `Date & round`] },
      { type: 'Investment Thesis', icon: 'ri-lightbulb-line',     getTitleFn: t => `Why ${t} Is a Compelling Investment`,                             getPointsFn: (_t, _a) => [`Market timing thesis`, `Unique insight or unfair advantage`, `Why now?`] },
      { type: 'Market Opportunity', icon: 'ri-global-line',       getTitleFn: t => `${t} Market Size`,                                                getPointsFn: (_t, _a) => [`TAM / SAM / SOM breakdown`, `Growth rate & drivers`, `Underserved segments`] },
      { type: 'Product',        icon: 'ri-computer-line',         getTitleFn: t => `${t} Product Overview`,                                           getPointsFn: (_t, _a) => [`Core product screenshots`, `Key differentiators`, `Technology moat`] },
      { type: 'Traction',       icon: 'ri-bar-chart-box-line',    getTitleFn: _t => `Traction & KPIs`,                                                getPointsFn: (_t, _a) => [`MRR / ARR growth chart`, `Customer count & retention', 'Net Promoter Score`] },
      { type: 'Financials',     icon: 'ri-file-chart-line',       getTitleFn: _t => `Financials & Projections`,                                       getPointsFn: (_t, _a) => [`Historical P&L`, `5-year revenue model`, `Key assumptions`] },
      { type: 'Valuation',      icon: 'ri-scales-3-line',         getTitleFn: _t => `Valuation Framework`,                                            getPointsFn: (_t, _a) => [`Comparable transactions`, `DCF analysis`, `Target valuation range`] },
      { type: 'Use of Funds',   icon: 'ri-money-dollar-circle-line', getTitleFn: _t => `Use of Proceeds`,                                             getPointsFn: (_t, _a) => [`Product & R&D: XX%`, `Sales & marketing: XX%`, `Operations & G&A: XX%`] },
      { type: 'Team',           icon: 'ri-team-line',             getTitleFn: _t => `Leadership Team`,                                                getPointsFn: (_t, _a) => [`CEO / Founder background`, `CTO / COO highlights`, `Advisory board`] },
      { type: 'Ask',            icon: 'ri-megaphone-line',         getTitleFn: _t => `The Ask`,                                                        getPointsFn: (_t, a) => [`Target raise amount`, `Lead investor opportunity for ${a}`, `Timeline to close`] },
    ],
    training: [
      { type: 'Title',          icon: 'ri-home-3-line',           getTitleFn: t => `${t} Training Program`,                                           getPointsFn: (_t, a) => [`For ${a}`, `Duration & format`, `Facilitator introduction`] },
      { type: 'Pre-Assessment', icon: 'ri-question-line',         getTitleFn: t => `What Do You Know About ${t}?`,                                    getPointsFn: (_t, _a) => [`Quick poll / survey`, `Baseline knowledge check`, `Set personal learning goals`] },
      { type: 'Objectives',     icon: 'ri-focus-3-line',          getTitleFn: t => `${t} Learning Goals`,                                             getPointsFn: (_t, _a) => [`By end of session you will…`, `Measurable outcomes`, `How success is assessed`] },
      { type: 'Module 1',       icon: 'ri-book-2-line',           getTitleFn: t => `Module 1: ${t} Fundamentals`,                                     getPointsFn: (_t, _a) => [`Core concept explained simply`, `Common terminology`, `Quick check-in question`] },
      { type: 'Module 2',       icon: 'ri-book-open-line',        getTitleFn: t => `Module 2: Applying ${t}`,                                         getPointsFn: (_t, _a) => [`Step-by-step process`, `Worked example`, `Practice scenario`] },
      { type: 'Activity',       icon: 'ri-gamepad-line',          getTitleFn: t => `${t} Group Activity`,                                             getPointsFn: (_t, _a) => [`Small group exercise (10 min)`, `Discussion prompt', 'Share-out format`] },
      { type: 'Best Practices', icon: 'ri-medal-line',            getTitleFn: t => `${t} Best Practices`,                                             getPointsFn: (_t, a) => [`Do this for ${a}`, `Avoid these common pitfalls`, `Expert tips`] },
      { type: 'Assessment',     icon: 'ri-checkbox-circle-line',  getTitleFn: t => `${t} Knowledge Check`,                                            getPointsFn: (_t, _a) => [`3-question quiz`, `Review correct answers`, `Identify knowledge gaps`] },
      { type: 'Action Plan',    icon: 'ri-flag-line',             getTitleFn: t => `${t} Personal Action Plan`,                                       getPointsFn: (_t, a) => [`Immediate next step for ${a}`, `30-day goal`, `Resources & support available`] },
      { type: 'Wrap-Up',        icon: 'ri-hand-heart-line',       getTitleFn: t => `${t} Training Recap`,                                             getPointsFn: (_t, _a) => [`Key takeaways`, `Q&A time`, `Feedback form link`] },
    ],
    conference: [
      { type: 'Title',          icon: 'ri-home-3-line',           getTitleFn: t => `${t}`,                                                            getPointsFn: (_t, a) => [`Speaker name & affiliation`, `Session time & track`, `Twitter / contact handle for ${a}`] },
      { type: 'About Me',       icon: 'ri-user-3-line',           getTitleFn: _t => `About the Speaker`,                                              getPointsFn: (_t, _a) => [`Professional background (60-sec bio)`, `Relevant experience', 'Why I care about this topic`] },
      { type: 'Agenda',         icon: 'ri-list-check-2',          getTitleFn: _t => `What We\'ll Cover Today`,                                        getPointsFn: (_t, _a) => [`Key theme 1`, `Key theme 2`, `Key takeaway & Q&A`] },
      { type: 'Hook / Story',   icon: 'ri-double-quotes-l',       getTitleFn: t => `A Story About ${t}`,                                              getPointsFn: (_t, a) => [`Opening anecdote that resonates with ${a}`, `The surprising insight`, `Bridge to the main argument`] },
      { type: 'Core Argument',  icon: 'ri-lightbulb-line',        getTitleFn: t => `The Case for ${t}`,                                               getPointsFn: (_t, _a) => [`Argument #1 with evidence`, `Argument #2 with evidence`, `Counter-argument & rebuttal`] },
      { type: 'Data & Research',icon: 'ri-bar-chart-box-line',    getTitleFn: t => `${t} by the Numbers`,                                             getPointsFn: (_t, _a) => [`Stat #1 (cite source)`, `Stat #2 (cite source)', 'What the data tells us`] },
      { type: 'Case Study',     icon: 'ri-briefcase-4-line',      getTitleFn: t => `${t} in the Real World`,                                          getPointsFn: (_t, _a) => [`Company / person example`, `What they did & why it worked`, `Replicable lesson`] },
      { type: 'Frameworks',     icon: 'ri-git-branch-line',       getTitleFn: t => `A Framework for ${t}`,                                            getPointsFn: (_t, _a) => [`Step 1 of the framework`, `Step 2 of the framework`, `How to apply it tomorrow`] },
      { type: 'Key Takeaways',  icon: 'ri-checkbox-circle-line',  getTitleFn: t => `Remember These About ${t}`,                                       getPointsFn: (_t, _a) => [`Takeaway #1 (tweet-sized)`, `Takeaway #2 (tweet-sized)', 'Takeaway #3 (tweet-sized)`] },
      { type: 'Call to Action', icon: 'ri-megaphone-line',         getTitleFn: _t => `What Will You Do Next?`,                                         getPointsFn: (_t, a) => [`One concrete action for ${a}`, `Resources to go deeper`, `Connect with me!`] },
    ],
  };

  const structures = structuresByGoal[goal] ?? structuresByGoal['pitch'];
  const colors = [
    '#e11d48', '#7c3aed', '#059669', '#d97706', '#0891b2', '#db2777',
    '#64748b', '#9333ea', '#0d9488', '#b45309',
  ];

  const slides: OutlineSlide[] = [];
  for (let i = 0; i < Math.min(slideCount, structures.length); i++) {
    const structure = structures[i % structures.length];
    slides.push({
      index: i + 1,
      title: structure.getTitleFn(mainTopic),
      type: structure.type,
      icon: structure.icon,
      points: structure.getPointsFn(mainTopic, audience),
      color: colors[i % colors.length],
    });
  }

  // Pad with extra content slides if slideCount > structures.length
  for (let i = structures.length; i < slideCount; i++) {
    slides.push({
      index: i + 1,
      title: `${mainTopic} — Part ${i - structures.length + 2}`,
      type: 'Content',
      icon: 'ri-file-list-3-line',
      points: [
        `Extended discussion on ${mainTopic}`,
        `Supporting data and examples`,
        `Additional context for ${audience}`,
      ],
      color: colors[i % colors.length],
    });
  }

  return slides;
}

interface GenerateAIOutlineModalProps {
  onClose: () => void;
  onUseOutline: () => void;
}

export default function GenerateAIOutlineModal({ onClose, onUseOutline }: GenerateAIOutlineModalProps) {
  const [topic, setTopic] = useState('');
  const [goal, setGoal] = useState('pitch');
  const [slideCount, setSlideCount] = useState(10);
  const [audience, setAudience] = useState('Investors');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [outline, setOutline] = useState<OutlineSlide[] | null>(null);
  const [expandedSlide, setExpandedSlide] = useState<number | null>(null);

  const currentGoal = PRESENTATION_GOALS.find(g => g.value === goal) ?? PRESENTATION_GOALS[0];

  const handleGenerate = () => {
    if (!topic.trim()) return;
    setIsGenerating(true);
    setGenerationProgress(0);
    setOutline(null);
    setExpandedSlide(null);

    // Simulated generation progress
    const steps = [15, 35, 55, 72, 88, 100];
    let stepIdx = 0;
    const interval = setInterval(() => {
      if (stepIdx < steps.length) {
        setGenerationProgress(steps[stepIdx]);
        stepIdx++;
      } else {
        clearInterval(interval);
        setIsGenerating(false);
        setOutline(generateOutline(topic, goal, slideCount, audience));
      }
    }, 320);
  };

  const handleReset = () => {
    setOutline(null);
    setIsGenerating(false);
    setGenerationProgress(0);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
      <div className="bg-white w-[760px] max-w-[96vw] max-h-[92vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-xl flex items-center justify-center">
              <i className="ri-sparkling-2-line text-white text-base"></i>
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-slate-900">Generate AI Outline</h2>
              <p className="text-xs text-slate-400 mt-0.5">Slide-by-slide structure, instantly generated</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>

          {/* ── Input form (shown when no outline yet) ── */}
          {!outline && !isGenerating && (
            <div className="p-6 space-y-5">
              {/* Topic */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">
                  <i className="ri-lightbulb-line text-violet-500 mr-1.5"></i>Presentation Topic *
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={e => setTopic(e.target.value)}
                  placeholder="e.g. AI in Healthcare, Q2 Sales Review, Climate Change..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200/40 transition-colors"
                  onKeyDown={e => { if (e.key === 'Enter' && topic.trim()) handleGenerate(); }}
                />
              </div>

              {/* Goal + Audience row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">
                    <i className="ri-target-line text-violet-500 mr-1.5"></i>Presentation Goal
                  </label>
                  <div className="grid grid-cols-1 gap-1.5">
                    {PRESENTATION_GOALS.map(g => (
                      <button
                        key={g.value}
                        onClick={() => setGoal(g.value)}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all cursor-pointer text-left ${
                          goal === g.value
                            ? 'border-transparent text-white'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                        style={goal === g.value ? { backgroundColor: g.color, borderColor: g.color } : {}}
                      >
                        <i className={`${g.icon} text-sm flex-shrink-0`} style={goal === g.value ? {} : { color: g.color }}></i>
                        <span className="text-xs font-semibold">{g.label}</span>
                        {goal === g.value && <i className="ri-check-line text-xs ml-auto"></i>}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Audience */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">
                      <i className="ri-group-line text-violet-500 mr-1.5"></i>Target Audience
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {AUDIENCE_TYPES.map(a => (
                        <button
                          key={a}
                          onClick={() => setAudience(a)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border whitespace-nowrap ${
                            audience === a
                              ? 'bg-slate-900 border-slate-900 text-white'
                              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400'
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Slide count */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">
                      <i className="ri-layout-grid-line text-violet-500 mr-1.5"></i>
                      Slide Count —{' '}
                      <span className="text-violet-600">{slideCount} slides</span>
                    </label>
                    <input
                      type="range"
                      min={5}
                      max={30}
                      value={slideCount}
                      onChange={e => setSlideCount(Number(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-violet-500"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>5</span><span>15</span><span>30</span>
                    </div>
                  </div>

                  {/* Preview card */}
                  <div
                    className="rounded-2xl p-4 border"
                    style={{ backgroundColor: `${currentGoal.color}10`, borderColor: `${currentGoal.color}30` }}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: currentGoal.color }}
                      >
                        <i className={`${currentGoal.icon} text-white text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-extrabold text-slate-800">{currentGoal.label}</p>
                        <p className="text-[10px] text-slate-400">for {audience}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Generating <strong className="text-slate-700">{slideCount} slides</strong>
                      {topic.trim() ? ` about "${topic.trim().slice(0, 28)}${topic.trim().length > 28 ? '…' : ''}"` : ' for your topic'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Generating progress ── */}
          {isGenerating && (
            <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-2xl flex items-center justify-center mb-5 animate-pulse">
                <i className="ri-sparkling-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-base font-extrabold text-slate-900 mb-2">Generating your outline…</h3>
              <p className="text-sm text-slate-500 mb-6">AI is crafting {slideCount} slides for &ldquo;{topic}&rdquo;</p>
              <div className="w-64 h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${generationProgress}%`,
                    background: 'linear-gradient(90deg, #7c3aed, #f43f5e)',
                  }}
                />
              </div>
              <p className="text-xs text-slate-400 font-mono">{generationProgress}%</p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {['Analyzing topic', 'Structuring flow', 'Writing slide titles', 'Generating key points', 'Optimizing order'].map((step, i) => (
                  <span
                    key={i}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                      generationProgress >= (i + 1) * 20
                        ? 'bg-violet-100 text-violet-700 line-through opacity-60'
                        : generationProgress >= i * 20
                        ? 'bg-violet-500 text-white animate-pulse'
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {step}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Outline results ── */}
          {outline && (
            <div className="p-6">
              {/* Summary header */}
              <div
                className="flex items-center justify-between p-4 rounded-2xl border mb-5"
                style={{ backgroundColor: `${currentGoal.color}08`, borderColor: `${currentGoal.color}25` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: currentGoal.color }}
                  >
                    <i className={`${currentGoal.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-extrabold text-slate-900">{topic}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {currentGoal.label} · {audience} · {outline.length} slides
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-xs"></i>Regenerate
                </button>
              </div>

              {/* Slide outline list */}
              <div className="space-y-2">
                {outline.map((slide, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-slate-100 overflow-hidden transition-all duration-200 hover:border-slate-200"
                  >
                    <button
                      onClick={() => setExpandedSlide(expandedSlide === i ? null : i)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      {/* Number */}
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-extrabold text-white"
                        style={{ backgroundColor: slide.color }}
                      >
                        {slide.index}
                      </div>
                      {/* Icon */}
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${slide.color}15` }}
                      >
                        <i className={`${slide.icon} text-sm`} style={{ color: slide.color }}></i>
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{slide.title}</p>
                        <span
                          className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full mt-0.5"
                          style={{ backgroundColor: `${slide.color}15`, color: slide.color }}
                        >
                          {slide.type}
                        </span>
                      </div>
                      {/* Expand chevron */}
                      <i
                        className={`ri-arrow-down-s-line text-slate-400 text-base flex-shrink-0 transition-transform duration-200 ${expandedSlide === i ? 'rotate-180' : ''}`}
                      ></i>
                    </button>

                    {/* Expanded bullet points */}
                    {expandedSlide === i && (
                      <div
                        className="px-14 py-3 border-t border-slate-100"
                        style={{ backgroundColor: `${slide.color}05` }}
                      >
                        <ul className="space-y-1.5">
                          {slide.points.map((point, pi) => (
                            <li key={pi} className="flex items-start gap-2 text-xs text-slate-600">
                              <div
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                                style={{ backgroundColor: slide.color }}
                              ></div>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0 flex items-center justify-between gap-3 bg-slate-50">
          {outline ? (
            <>
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <i className="ri-information-line text-slate-400"></i>
                Click any slide to preview its key points
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line text-sm"></i>Edit Inputs
                </button>
                <button
                  onClick={onUseOutline}
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 cursor-pointer transition-all whitespace-nowrap"
                >
                  <i className="ri-magic-line text-sm"></i>Create Presentation
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-slate-400">
                {isGenerating ? 'Generating…' : 'Press Enter or click Generate to get your outline'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={!topic.trim() || isGenerating}
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all whitespace-nowrap"
                >
                  <i className="ri-sparkling-2-line text-sm"></i>Generate Outline
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
